import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useDispatch, useSelector } from "react-redux";
import { getRiskStatus } from "../../../../redux/action/Stats";
import NoPieData from "../../../common/components/NoPieData";
import { ShieldAlert } from "lucide-react";
ChartJS.register(ArcElement, Tooltip, Legend);
const colorMapping = {
  "HIGH RISK": "#DC2626", // red
  "MODERATE RISK": "#FF7439", // orange
  "LOW RISK": "#FFEE33", // yellow
  "NO RISK": "#B7E049", // green
};

const RiskDistributionChart = () => {
  const [type, setType] = useState("lastThreeMonth");
  const dispatch = useDispatch();
  const { data, loading } = useSelector((state) => state.GET_RISK_STATUS);
  const [chartData, setChartData] = useState(null);

  const getRiskStatusData = useCallback(() => {
    let payload = {};
    if (type === "currentWeek") {
      payload = JSON.stringify({
        currentWeek: true,
      });
    } else if (type === "currentMonth") {
      payload = JSON.stringify({
        currentMonth: true,
      });
    } else if (type === "lastThreeMonth") {
      payload = JSON.stringify({
        lastThreeMonth: true,
      });
    }
    dispatch(getRiskStatus(payload));
  }, [dispatch, type]);

  useEffect(() => {
    getRiskStatusData();
  }, [getRiskStatusData]);

  useEffect(() => {
    if (type === "currentMonth") {
      if (data && data.month && Array.isArray(data.month)) {
        const labels = data.month.map((item) => item.name.replace("_", " "));
        const values = data.month.map((item) => item.value);
        const backgroundColors = labels.map(
          (label) => colorMapping[label] || "#CCCCCC" // fallback for unexpected labels
        );
        const newChartData = {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: backgroundColors,
              borderWidth: 1,
            },
          ],
        };
        setChartData(newChartData);
      }
    } else if (type === "currentWeek") {
      if (data && data.week && Array.isArray(data.week)) {
        const labels = data.week.map((item) => item.name.replace("_", " "));
        const values = data.week.map((item) => item.value);
        const backgroundColors = labels.map(
          (label) => colorMapping[label] || "#CCCCCC" // fallback for unexpected labels
        );

        const newChartData = {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: backgroundColors,
              borderWidth: 1,
            },
          ],
        };
        setChartData(newChartData);
      }
    } else if (type === "lastThreeMonth") {
      if (data && data.threeMonths && Array.isArray(data.threeMonths)) {
        const labels = data.threeMonths.map((item) =>
          item.name.replace("_", " ")
        );
        const values = data.threeMonths.map((item) => item.value);
        const backgroundColors = labels.map(
          (label) => colorMapping[label] || "#CCCCCC" // fallback for unexpected labels
        );

        const newChartData = {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: backgroundColors,
              borderWidth: 1,
            },
          ],
        };
        setChartData(newChartData);
      }
    }
  }, [data, type]);

  const isChartEmpty = useMemo(() => {
    if (!chartData) return true;
    const values = chartData.datasets[0].data;
    return values.every((val) => val === 0);
  }, [chartData]);

  return (
    <div className="bg-white rounded-2xl w-full h-full border-[1px] border-solid border-[#E2E8F0] shadow-sm p-[24px] min-w-0">
      {/* Header */}
      <div className="flex justify-between items-center gap-2 -mx-[24px] -mt-[24px] px-[24px] py-[14px] bg-[#E7EDF5] rounded-t-[15px]">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-[36px] h-[36px] rounded-xl bg-white text-[#E11D48] flex-shrink-0">
            <ShieldAlert size={18} />
          </div>
          <h2 className="text-base font-semibold text-[#171717] font-poppins mb-0">
            Risk Status
          </h2>
        </div>

        {/* Dropdown */}
        <div className="relative inline-block">
          <select
            value={type} // <-- this is the fix
            className="border-[1px] border-solid border-[#E2E8F0] font-poppins text-xs rounded-xl pl-[12px] pr-[34px] py-[6px] appearance-none bg-white text-[#2563EB] font-medium cursor-pointer hover:border-[#CBD5E1]"
            onChange={(e) => setType(e.target.value)}
          >
            <option value={"currentWeek"}>This Week</option>
            <option value={"currentMonth"}>This Month</option>
            <option value={"lastThreeMonth"}>3 Month</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
            <svg
              className="w-3.5 h-3.5 text-[#2563EB]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      <div className="mt-4" />

      <div>
        {loading ? (
          <div className="text-center text-gray-500 mt-20">Loading...</div>
        ) : !chartData || isChartEmpty ? (
          <NoPieData />
        ) : (
          <div className="w-[240px] h-[240px] mx-auto max-w-full">
            <Pie
              data={chartData}
              options={{
                plugins: {
                  legend: {
                    display: false,
                  },
                },
              }}
            />
          </div>
        )}
      </div>

      {!loading && chartData && !isChartEmpty && (
        <div className="flex flex-wrap justify-center mt-4 text-xs gap-2">
          {chartData.labels.map((label, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#F8FAFC] border-[1px] border-solid border-[#F1F5F9] text-[#334155] font-poppins font-medium"
            >
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: chartData.datasets[0].backgroundColor[index] }}
              ></span>
              {label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RiskDistributionChart;